// Generate a starter S1000D BREX skeleton (.brex.xml) from an estimate's brexPlan.
import { BR_CATEGORIES, BR_CATEGORY_BREX_PATHS, BR_CATEGORY_DM_MAP } from "./s1000dData";

function xmlEscape(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeFilename(name) {
  return (name || "estimate").replace(/[^a-z0-9-_]+/gi, "_");
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Turn a free-text system name into an S1000D-style applicability property
// value: uppercase, alphanumerics only, max 12 chars.  "Flight Controls" → "FLIGHTCONTRO".
function propertyValue(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 12) || "UNKNOWN";
}

// Inspect the SBS plan and return three sorted lists of unique system names:
//   all          — every named System (level 0) that has at least one item
//   general      — Systems that contribute any General items at any level
//   repairable   — Systems that contribute any Repairable items at any level
function summariseSbsForApplic(sbs) {
  const out = { all: [], general: [], repairable: [] };
  if (!sbs || !Array.isArray(sbs.systems)) return out;
  const allSet = new Set();
  const genSet = new Set();
  const repSet = new Set();
  for (const sys of sbs.systems) {
    const name = String(sys.name || "").trim();
    if (!name) continue;
    let hasItems = false, hasGen = false, hasRep = false;
    const addAt = (gen, rep) => {
      if (gen > 0) { hasGen = true; hasItems = true; }
      if (rep > 0) { hasRep = true; hasItems = true; }
    };
    addAt(Number(sys.generalQty) || 0, Number(sys.repairableQty) || 0);
    for (const sub of (sys.subsystems || [])) {
      addAt(Number(sub.generalQty) || 0, Number(sub.repairableQty) || 0);
      for (const ss of (sub.subsubsystems || [])) {
        addAt(Number(ss.generalQty) || 0, Number(ss.repairableQty) || 0);
      }
    }
    if (hasItems) allSet.add(name);
    if (hasGen) genSet.add(name);
    if (hasRep) repSet.add(name);
  }
  out.all = [...allSet].sort();
  out.general = [...genSet].sort();
  out.repairable = [...repSet].sort();
  return out;
}

// Pick which SBS system list applies to a BREX category.  If the category
// is mapped to the SBS general DM type, narrow to general systems; if to the
// repairable DM type, narrow to repairable; otherwise default to all.
function scopeForCategory(catId, sbsSummary, generalDmType, repairableDmType) {
  const mapped = BR_CATEGORY_DM_MAP[catId] || [];
  const targetsGeneral = mapped.includes(generalDmType);
  const targetsRepairable = mapped.includes(repairableDmType);
  if (targetsGeneral && !targetsRepairable) return sbsSummary.general;
  if (targetsRepairable && !targetsGeneral) return sbsSummary.repairable;
  return sbsSummary.all;
}

// Build an <applic> element for a list of system names.  When the list is
// empty, returns the legacy "All" applicability so the XML stays valid.
function buildApplicBlock(systems, opts = {}) {
  const indent = opts.indent || "          ";
  if (!systems || systems.length === 0) {
    return `${indent}<applic><displayText><simplePara>All</simplePara></displayText></applic>`;
  }
  const display = `Applicable to: ${systems.join(", ")}`;
  const values = systems.map(propertyValue).join("|");
  return (
    `${indent}<applic>\n` +
    `${indent}  <displayText><simplePara>${xmlEscape(display)}</simplePara></displayText>\n` +
    `${indent}  <assert applicPropertyIdent="sbs-system" applicPropertyType="prodattr" applicPropertyValues="${xmlEscape(values)}"/>\n` +
    `${indent}</applic>`
  );
}

export function buildBrexSkeletonXml(estimate, brex) {
  const plan = estimate.brexPlan || {};
  const issue = plan.s1000dIssue || "6.0";
  const project = estimate.projectName || "Untitled";
  const generated = new Date().toISOString();
  const includedCats = brex.categoryRows.filter((c) => c.included);

  const sbsSummary = summariseSbsForApplic(estimate.sbsPlan);
  const genDm = estimate.sbsPlan?.generalDmType || "040";
  const repDm = estimate.sbsPlan?.repairableDmType || "520";
  const topApplic = buildApplicBlock(sbsSummary.all, { indent: "      " });

  const ruleGroups = includedCats.map((cat) => {
    const path = BR_CATEGORY_BREX_PATHS[cat.id] || "//*";
    const rulesPerCat = Math.min(Math.max(0, cat.dps), 6); // up to 6 placeholders; 0 → empty group
    const scopeSystems = scopeForCategory(cat.id, sbsSummary, genDm, repDm);
    const ruleApplic = buildApplicBlock(scopeSystems, { indent: "          " });
    const stubs = Array.from({ length: rulesPerCat }, (_, i) => `
        <structureObjectRule>
          <objectPath allowedObjectFlag="2">${xmlEscape(path)}</objectPath>
          <objectUse>TODO ${xmlEscape(cat.name)} rule ${i + 1} of ${cat.dps} (placeholder)</objectUse>
${ruleApplic}
        </structureObjectRule>`).join("");
    const emptyNote = rulesPerCat === 0
      ? `\n        <!-- 0 decision points defined for this category in the selected issue -->`
      : "";
    const scopeNote = scopeSystems.length > 0
      ? `\n      <!-- Scope: ${xmlEscape(scopeSystems.join(", "))} -->`
      : "";
    return `
      <!-- ===== ${xmlEscape(cat.name)} — ${cat.dps} decision point(s) ===== -->${scopeNote}
      <structureObjectRuleGroup id="brex-${xmlEscape(cat.id)}">${emptyNote}${stubs}
      </structureObjectRuleGroup>`;
  }).join("");

  const sbsScopeComment = sbsSummary.all.length > 0
    ? `\n  Scope     : ${sbsSummary.all.length} system(s) from SBS — ${xmlEscape(sbsSummary.all.join(", "))}`
    : "";

  return `<?xml version="1.0" encoding="UTF-8"?>
<!--
  S1000D BREX SKELETON — generated by S1000D Cost Estimator
  Project   : ${xmlEscape(project)}
  S1000D    : Issue ${xmlEscape(issue)}
  Generated : ${xmlEscape(generated)}
  Included  : ${includedCats.length} of ${brex.categoryRows.length} categories · ${brex.totalDps} decision points${sbsScopeComment}
  NOTE      : placeholder objectPaths are illustrative. Replace with rules
              consistent with your chosen S1000D Issue ${xmlEscape(issue)} schema.
-->
<dmodule
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xmlns:xlink="http://www.w3.org/1999/xlink">
  <identAndStatusSection>
    <dmAddress>
      <dmIdent>
        <dmCode modelIdentCode="PROJECT" systemDiffCode="A" systemCode="00" subSystemCode="0"
                subSubSystemCode="0" assyCode="00" disassyCode="00" disassyCodeVariant="A"
                infoCode="022" infoCodeVariant="A" itemLocationCode="D" learnCode="" learnEventCode=""/>
        <language languageIsoCode="en" countryIsoCode="US"/>
        <issueInfo issueNumber="000" inWork="01"/>
      </dmIdent>
      <dmAddressItems>
        <issueDate year="${new Date().getFullYear()}" month="${String(new Date().getMonth() + 1).padStart(2, "0")}" day="${String(new Date().getDate()).padStart(2, "0")}"/>
        <dmTitle><techName>${xmlEscape(project)}</techName><infoName>BREX skeleton</infoName></dmTitle>
      </dmAddressItems>
    </dmAddress>
    <dmStatus issueType="new">
      <security securityClassification="01"/>
      <responsiblePartnerCompany><enterpriseName>Generated by S1000D Cost Estimator</enterpriseName></responsiblePartnerCompany>
      <originator><enterpriseName>S1000D Cost Estimator</enterpriseName></originator>
${topApplic}
      <brexDmRef><dmRef><dmRefIdent><dmCode modelIdentCode="S1000D" systemDiffCode="A" systemCode="00"
        subSystemCode="0" subSubSystemCode="0" assyCode="00" disassyCode="00" disassyCodeVariant="A"
        infoCode="022" infoCodeVariant="A" itemLocationCode="D" learnCode="" learnEventCode=""/></dmRefIdent></dmRef></brexDmRef>
      <qualityAssurance><unverified/></qualityAssurance>
    </dmStatus>
  </identAndStatusSection>
  <content>
    <brex>
      <contextRules>${ruleGroups}
      </contextRules>
    </brex>
  </content>
</dmodule>
`;
}

export function exportBrexSkeleton(estimate, brex) {
  const xml = buildBrexSkeletonXml(estimate, brex);
  const blob = new Blob([xml], { type: "application/xml;charset=utf-8;" });
  triggerDownload(blob, `${safeFilename(estimate.projectName)}_BREX_skeleton.brex.xml`);
}

// Build a Markdown summary of BR categories → DM types coverage
export function brexDmCoverage(estimate, brex) {
  // estimate.dmMatrix tells which DM types have qty > 0
  const usedDmTypes = new Set();
  for (const code of Object.keys(estimate.dmMatrix || {})) {
    const row = estimate.dmMatrix[code];
    const total = (row?.simple?.qty || 0) + (row?.intermediate?.qty || 0) + (row?.complex?.qty || 0);
    if (total > 0) usedDmTypes.add(code);
  }
  return BR_CATEGORIES.map((cat) => {
    const mapped = (estimate?._dmMap || {})[cat.id]; // optional override
    const dmTypes = mapped || [];
    return { id: cat.id, name: cat.name, dmTypes, usedDmTypes };
  });
}
